import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Plus,
  Upload,
  Loader2,
  Search,
  Trash2,
  Edit,
  FileSpreadsheet,
} from 'lucide-react';
import Layout from '../components/Layout';
import authService from '../services/authService';
import barStockQuoteService from '../services/barStockQuoteService';

const BarStockQuotesPage = () => {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [replaceExisting, setReplaceExisting] = useState(false);
const [filters, setFilters] = useState({
diameter_min: '',
diameter_max: '',
supplier_name: '',
});
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 20,
    total: 0,
    total_pages: 0,
  });

  const isEngineerOrAdmin = ['admin', 'engineer'].includes(
    authService.getCurrentUser()?.role
  );

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        page_size: pagination.page_size,
      };

      if (filters.diameter_min) params.diameter_min = parseFloat(filters.diameter_min);
      if (filters.diameter_max) params.diameter_max = parseFloat(filters.diameter_max);
      if (filters.supplier_name) params.supplier_name = filters.supplier_name;

      const response = await barStockQuoteService.getQuotes(params);
      setQuotes(response.items || []);
      setPagination(prev => ({
        ...prev,
        total: response.total || 0,
        total_pages: response.total_pages || 0,
      }));
    } catch (err) {
      console.error('Failed to fetch quotes:', err);
      setError('获取棒料报价失败');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.page_size]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploadLoading(true);
    try {
      const result = await barStockQuoteService.uploadQuoteFile(selectedFile);
      setExtractedData(result);
    } catch (err) {
      console.error('Upload failed:', err);
      setError('文件上传失败');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleImport = async () => {
    if (!extractedData?.items?.length) return;

    try {
      await barStockQuoteService.importQuotes(extractedData.items, replaceExisting);
setUploadDialogOpen(false);
setSelectedFile(null);
setExtractedData(null);
      setReplaceExisting(false);
    } catch (err) {
      console.error('Import failed:', err);
      setError('导入失败');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('确定要删除这个报价吗？')) return;

    try {
      await barStockQuoteService.deleteQuote(id);
      fetchQuotes();
    } catch (err) {
      console.error('Delete failed:', err);
      setError('删除失败');
    }
  };

  const formatPrice = (price) => {
    return price?.toFixed(2) || '-';
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">棒料报价管理</h1>
          <div className="flex gap-2">
            {isEngineerOrAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => setUploadDialogOpen(true)}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  上传报价
                </Button>
                <Button onClick={() => navigate('/bar-stock-quotes/new')}>
                  <Plus className="w-4 h-4 mr-2" />
                  手动添加
                </Button>
              </>
            )}
          </div>
        </div>

        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-4 gap-4">
              <div>
                <Label>最小直径 (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={filters.diameter_min}
                  onChange={(e) => {
                    setFilters({ ...filters, diameter_min: e.target.value });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  placeholder="2.5"
                />
              </div>
              <div>
                <Label>最大直径 (mm)</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={filters.diameter_max}
                  onChange={(e) => {
                    setFilters({ ...filters, diameter_max: e.target.value });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  placeholder="20.0"
                />
              </div>
              <div>
                <Label>供应商</Label>
                <Input
                  value={filters.supplier_name}
                  onChange={(e) => {
                    setFilters({ ...filters, supplier_name: e.target.value });
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  placeholder="供应商名称"
                />
              </div>
              <div className="flex items-end">
                <Button onClick={fetchQuotes} className="w-full">
                  <Search className="w-4 h-4 mr-2" />
                  搜索
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quotes Table */}
        <Card>
          <CardHeader>
            <CardTitle>报价列表</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin" />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">规格</th>
                      <th className="text-left py-3 px-4">直径 (mm)</th>
                      <th className="text-left py-3 px-4">长度 (mm)</th>
                      <th className="text-right py-3 px-4">含税单价</th>
                      <th className="text-left py-3 px-4">直径 (mm)</th>
                      <th className="text-right py-3 px-4">含税单价</th>
                      <th className="text-right py-3 px-4">未税单价</th>
                      <th className="text-right py-3 px-4">每毫米单价</th>
                      <th className="text-left py-3 px-4">供应商</th>
                      <th className="text-left py-3 px-4">报价日期</th>
                      {isEngineerOrAdmin && (
                        <th className="text-center py-3 px-4">操作</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {quotes.map((quote) => (
                      <tr key={quote._id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{quote.specification}</td>
                        <td className="py-3 px-4">{quote.diameter}</td>
                        <td className="py-3 px-4">{quote.length || 310}</td>
                        <td className="py-3 px-4 text-right">
                          ¥{formatPrice(quote.unit_price_tax_included)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          ¥{formatPrice(quote.unit_price_tax_excluded)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          ¥{formatPrice(quote.price_per_mm)}
                        </td>
                        <td className="py-3 px-4">{quote.supplier_name}</td>
                        <td className="py-3 px-4">
                          {new Date(quote.quote_date).toLocaleDateString()}
                        </td>
                        {isEngineerOrAdmin && (
                          <td className="py-3 px-4 text-center">
                            <div className="flex justify-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  navigate(`/bar-stock-quotes/${quote._id}/edit`)
                                }
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(quote._id)}
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.total_pages > 1 && (
              <div className="flex justify-center mt-4 gap-2">
                {Array.from({ length: pagination.total_pages }, (_, i) => (
                  <Button
                    key={i + 1}
                    variant={pagination.page === i + 1 ? 'default' : 'outline'}
                    size="sm"
                    onClick={() =>
                      setPagination({ ...pagination, page: i + 1 })
                    }
                  >
                    {i + 1}
                  </Button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        {uploadDialogOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl mx-4">
              <CardHeader>
                <CardTitle>上传报价文件</CardTitle>
              </CardHeader>
              <CardContent>
                {!extractedData ? (
                  <div className="space-y-4">
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center">
                      <FileSpreadsheet className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <p className="text-gray-600 mb-4">
                        支持 Excel (.xlsx, .xls) 或 PDF 文件
                      </p>
                      <Input
                        type="file"
                        accept=".xlsx,.xls,.pdf"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="quote-file"
                      />
                      <Label htmlFor="quote-file" className="cursor-pointer">
                        <Button variant="outline" asChild>
                          <span>选择文件</span>
                        </Button>
                      </Label>
                      {selectedFile && (
                        <p className="mt-2 text-sm text-gray-600">
                          已选择: {selectedFile.name}
                        </p>
                      )}
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setUploadDialogOpen(false);
                          setSelectedFile(null);
                        }}
                      >
                        取消
                      </Button>
                      <Button
                        onClick={handleUpload}
                        disabled={!selectedFile || uploadLoading}
                      >
                        {uploadLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            处理中...
                          </>
                        ) : (
                          '上传并提取'
                        )}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-green-600">
                      成功提取 {extractedData.items?.length || 0} 条报价记录
                    </p>
                    <div className="max-h-64 overflow-y-auto border rounded">
                      <table className="w-full">
                        <thead className="bg-gray-50 sticky top-0">
                          <tr>
                            <th className="text-left py-2 px-3">规格</th>
                            <th className="text-right py-2 px-3">单价</th>
                            <th className="text-left py-2 px-3">供应商</th>
                          </tr>
                        </thead>
                        <tbody>
                          {extractedData.items?.map((item, idx) => (
                            <tr key={idx} className="border-b">
                              <td className="py-2 px-3">{item.specification}</td>
                              <td className="py-2 px-3 text-right">
                                ¥{item.unit_price_tax_included}
                              </td>
                              <td className="py-2 px-3">{item.supplier_name}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center space-x-2 py-2">
                      <Checkbox
                        id="replace-existing"
                        checked={replaceExisting}
                        onCheckedChange={(checked) => setReplaceExisting(checked)}
                      />
                      <Label htmlFor="replace-existing" className="text-sm cursor-pointer">
                        替换现有同规格报价（如果不勾选，已存在的报价将被跳过）
                      </Label>
                    </div>
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setExtractedData(null);
                          setSelectedFile(null);
                          setReplaceExisting(false);
                        }}
                      >
                        重新上传
                      </Button>
                      <Button onClick={handleImport}>确认导入</Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default BarStockQuotesPage;
